import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTheme } from '@/contexts/ThemeContext';

export interface ImageDialogInsertData {
  src: string;
  alt: string;
  width?: string;
  height?: string;
}

export interface ImageDialogProps {
  open: boolean;
  initialSrc?: string;
  initialAlt?: string;
  initialWidth?: string;
  initialHeight?: string;
  theme?: string;
  onInsert: (data: ImageDialogInsertData) => void;
  onCancel: () => void;
}

export function ImageDialog({
  open,
  initialSrc = '',
  initialAlt = '',
  initialWidth = '',
  initialHeight = '',
  theme: themeProp,
  onInsert,
  onCancel,
}: ImageDialogProps) {
  const { theme: globalTheme } = useTheme();
  const theme = themeProp ?? globalTheme;

  const [src, setSrc] = useState('');
  const [alt, setAlt] = useState('');
  const [width, setWidth] = useState('');
  const [height, setHeight] = useState('');
  const [srcError, setSrcError] = useState('');
  const [maintainAspectRatio, setMaintainAspectRatio] = useState(true);

  const originalWidthRef = useRef(0);
  const originalHeightRef = useRef(0);
  const srcInputRef = useRef<HTMLInputElement>(null);
  // Guard to prevent the width watcher from updating height when we
  // are programmatically setting both dimensions (e.g. on image load
  // or when the dialog opens).
  const suppressAspectRatioRef = useRef(false);

  const validateSrc = useCallback((value: string): string => {
    if (!value.trim()) {
      return '';
    }
    const urlPattern = /^(https?:\/\/|data:image\/|\/)/i;
    if (!urlPattern.test(value.trim())) {
      return 'Please enter a valid image URL';
    }
    return '';
  }, []);

  const isValid = useMemo(() => {
    return src.trim().length > 0 && !srcError;
  }, [src, srcError]);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      suppressAspectRatioRef.current = true;
      setSrc(initialSrc);
      setAlt(initialAlt);
      setWidth(initialWidth);
      setHeight(initialHeight);
      setSrcError('');
      setMaintainAspectRatio(true);
      originalWidthRef.current = 0;
      originalHeightRef.current = 0;
      // Allow the suppression to clear after state settles
      requestAnimationFrame(() => {
        suppressAspectRatioRef.current = false;
      });
    }
  }, [open, initialSrc, initialAlt, initialWidth, initialHeight]);

  // Focus src input when dialog opens
  useEffect(() => {
    if (open) {
      const raf = requestAnimationFrame(() => {
        srcInputRef.current?.focus();
      });
      return () => cancelAnimationFrame(raf);
    }
  }, [open]);

  // Validate src and load image dimensions when src changes
  useEffect(() => {
    setSrcError(validateSrc(src));

    if (!src.trim() || validateSrc(src)) return;

    const img = new Image();
    img.onload = () => {
      originalWidthRef.current = img.naturalWidth;
      originalHeightRef.current = img.naturalHeight;
      setWidth((prev) => prev || img.naturalWidth.toString());
      setHeight((prev) => prev || img.naturalHeight.toString());
    };
    img.onerror = () => {
      // Image failed to load, but don't show error - might be CORS
    };
    img.src = src;
  }, [src, validateSrc]);

  // Maintain aspect ratio when width changes
  useEffect(() => {
    if (suppressAspectRatioRef.current) return;
    if (
      maintainAspectRatio &&
      originalWidthRef.current &&
      originalHeightRef.current &&
      width
    ) {
      const ratio = originalHeightRef.current / originalWidthRef.current;
      setHeight(Math.round(parseInt(width) * ratio).toString());
    }
  }, [width, maintainAspectRatio]);

  const handleInsert = useCallback(() => {
    if (!isValid) return;

    onInsert({
      src: src.trim(),
      alt: alt.trim(),
      width: width || undefined,
      height: height || undefined,
    });
    onCancel();
  }, [isValid, src, alt, width, height, onInsert, onCancel]);

  const handleClose = useCallback(() => {
    onCancel();
  }, [onCancel]);

  const handleKeydown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose();
      } else if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        handleInsert();
      }
    },
    [handleClose, handleInsert]
  );

  const handleBackdropClick = useCallback(
    (event: React.MouseEvent) => {
      if (event.target === event.currentTarget) {
        handleClose();
      }
    },
    [handleClose]
  );

  if (!open) return null;

  const dialogClasses = [
    'kst-dialog',
    'kst-image-dialog',
    theme || '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className="kst-dialog-overlay"
      onClick={handleBackdropClick}
      onKeyDown={handleKeydown}
    >
      <div className={dialogClasses} role="dialog" aria-labelledby="image-dialog-title">
        <div className="kst-dialog-header">
          <h2 id="image-dialog-title">Insert Image</h2>
          <button className="kst-dialog-close" onClick={handleClose} aria-label="Close">
            <i className="fas fa-times"></i>
          </button>
        </div>
        <div className="kst-dialog-body">
          <div className="kst-form-group">
            <label htmlFor="image-src">
              Image URL <span className="required">*</span>
            </label>
            <input
              ref={srcInputRef}
              id="image-src"
              type="text"
              value={src}
              onChange={(e) => setSrc(e.target.value)}
              placeholder="https://example.com/image.jpg"
              className={srcError ? 'has-error' : ''}
            />
            {srcError && <span className="error-message">{srcError}</span>}
          </div>
          <div className="kst-form-group">
            <label htmlFor="image-alt">Alt Text</label>
            <input
              id="image-alt"
              type="text"
              value={alt}
              onChange={(e) => setAlt(e.target.value)}
              placeholder="Image description"
            />
          </div>
          <div className="kst-form-row">
            <div className="kst-form-group half">
              <label htmlFor="image-width">Width (px)</label>
              <input
                id="image-width"
                type="number"
                value={width}
                onChange={(e) => setWidth(e.target.value)}
                placeholder="Auto"
                min={1}
              />
            </div>
            <div className="kst-form-group half">
              <label htmlFor="image-height">Height (px)</label>
              <input
                id="image-height"
                type="number"
                value={height}
                onChange={(e) => setHeight(e.target.value)}
                placeholder="Auto"
                min={1}
              />
            </div>
          </div>
          <div className="kst-form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                checked={maintainAspectRatio}
                onChange={(e) => setMaintainAspectRatio(e.target.checked)}
              />
              Maintain aspect ratio
            </label>
          </div>
          {src && !srcError && (
            <div className="image-preview">
              <img
                src={src}
                alt={alt}
                style={{ maxWidth: '200px', maxHeight: '150px' }}
              />
            </div>
          )}
        </div>
        <div className="kst-dialog-footer">
          <button className="kst-btn kst-btn-secondary" onClick={handleClose}>
            Cancel
          </button>
          <button
            className="kst-btn kst-btn-primary"
            onClick={handleInsert}
            disabled={!isValid}
          >
            Insert
          </button>
        </div>
      </div>
    </div>
  );
}

(window as any).KstImageDialogReact = ImageDialog;
